import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Star, Edit2, Trash2, Check, X, Home } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const LABEL_ICONS = {
  Home: '🏠',
  Work: '🏢',
  Airport: '✈️',
  Gym: '🏋️',
  School: '🏫',
  Other: '📍',
};

const LABEL_OPTIONS = ['Home', 'Work', 'Airport', 'Gym', 'School', 'Other'];

const AddressManagement = ({ onAutofill }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ label: 'Home', address: '' });
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error: fetchError } = await supabase?.from('customer_addresses')?.select('*')?.eq('user_id', user?.id)?.order('is_favorite', { ascending: false })?.order('use_count', { ascending: false })?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAddresses(data || []);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
      setError('Failed to load addresses.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({ label: 'Home', address: '' });
    setShowForm(true);
  };

  const handleOpenEdit = (addr) => {
    setEditingId(addr?.id);
    setForm({ label: addr?.label, address: addr?.address });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ label: 'Home', address: '' });
  };

  const handleSave = async () => {
    if (!form?.address?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (editingId) {
        const { error: updateError } = await supabase?.from('customer_addresses')?.update({ label: form?.label, address: form?.address?.trim() })?.eq('id', editingId)?.eq('user_id', user?.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase?.from('customer_addresses')?.insert({ user_id: user?.id, label: form?.label, address: form?.address?.trim() });
        if (insertError) throw insertError;
      }

      await fetchAddresses();
      handleCancelForm();
    } catch (err) {
      console.error('Failed to save address:', err);
      setError('Failed to save address. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFavorite = async (addr) => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { error: updateError } = await supabase?.from('customer_addresses')?.update({ is_favorite: !addr?.is_favorite })?.eq('id', addr?.id)?.eq('user_id', user?.id);

      if (updateError) throw updateError;
      setAddresses(prev => prev?.map(a =>
        a?.id === addr?.id ? { ...a, is_favorite: !a?.is_favorite } : a
      )?.sort((a, b) => (b?.is_favorite ? 1 : 0) - (a?.is_favorite ? 1 : 0)));
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { error: deleteError } = await supabase?.from('customer_addresses')?.delete()?.eq('id', id)?.eq('user_id', user?.id);

      if (deleteError) throw deleteError;
      setAddresses(prev => prev?.filter(a => a?.id !== id));
    } catch (err) {
      console.error('Failed to delete address:', err);
      setError('Failed to delete address.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAutofill = async (addr) => {
    // Increment use_count
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.from('customer_addresses')?.update({ use_count: (addr?.use_count || 0) + 1 })?.eq('id', addr?.id)?.eq('user_id', user?.id);
      }
    } catch (err) {
      // non-critical
    }
    onAutofill?.(addr);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#1a365d]/10 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#1a365d]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#1a365d]">Saved Addresses</h3>
            <p className="text-xs text-slate-400">Tap to autofill booking</p>
          </div>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a365d] text-white text-xs font-medium rounded-lg hover:bg-[#2d5a87] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add
        </button>
      </div>
      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
          {error}
        </div>
      )}
      {/* Add/Edit Form */}
      {showForm && (
        <div className="px-4 py-4 bg-slate-50 border-b border-slate-100">
          <p className="text-xs font-semibold text-[#1a365d] mb-3">
            {editingId ? 'Edit Address' : 'New Address'}
          </p>
          {/* Label selector */}
          <div className="flex gap-2 flex-wrap mb-3">
            {LABEL_OPTIONS?.map(lbl => (
              <button
                key={lbl}
                onClick={() => setForm(f => ({ ...f, label: lbl }))}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                  form?.label === lbl
                    ? 'bg-[#1a365d] text-white border-[#1a365d]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-[#1a365d]/40'
                }`}
              >
                <span>{LABEL_ICONS?.[lbl] || '📍'}</span>
                {lbl}
              </button>
            ))}
          </div>
          {/* Address input */}
          <input
            type="text"
            value={form?.address}
            onChange={e => setForm(f => ({ ...f, address: e?.target?.value }))}
            placeholder="Enter full address..."
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a365d]/20 focus:border-[#1a365d] bg-white text-[#1a365d] placeholder-slate-400 mb-3"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form?.address?.trim()}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1a365d] text-white text-xs font-medium rounded-lg hover:bg-[#2d5a87] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              {editingId ? 'Update' : 'Save'}
            </button>
            <button
              onClick={handleCancelForm}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Address List */}
      <div className="divide-y divide-slate-50">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#1a365d] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : addresses?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
              <MapPin className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-500">No saved addresses</p>
            <p className="text-xs text-slate-400 mt-1">Add addresses for quick booking autofill</p>
          </div>
        ) : (
          addresses?.map(addr => (
            <div key={addr?.id} className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors group">
              {/* Icon */}
              <div className="w-9 h-9 rounded-xl bg-[#1a365d]/8 flex items-center justify-center flex-shrink-0 text-base">
                {LABEL_ICONS?.[addr?.label] || '📍'}
              </div>

              {/* Info — tap to autofill */}
              <button
                onClick={() => handleAutofill(addr)}
                className="flex-1 min-w-0 text-left"
                title="Tap to autofill booking"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-[#1a365d]">{addr?.label}</span>
                  {addr?.is_favorite && (
                    <Star className="w-3 h-3 text-[#ffd700] fill-[#ffd700]" />
                  )}
                  {addr?.use_count > 0 && (
                    <span className="text-[10px] text-slate-400">· used {addr?.use_count}×</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{addr?.address}</p>
                <p className="text-[10px] text-[#1a365d]/50 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  Tap to autofill →
                </p>
              </button>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleToggleFavorite(addr)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    addr?.is_favorite
                      ? 'text-[#ffd700] bg-[#ffd700]/10'
                      : 'text-slate-300 hover:text-[#ffd700] hover:bg-[#ffd700]/10'
                  }`}
                  title={addr?.is_favorite ? 'Remove from favorites' : 'Mark as favorite'}
                >
                  <Star className={`w-3.5 h-3.5 ${addr?.is_favorite ? 'fill-[#ffd700]' : ''}`} />
                </button>
                <button
                  onClick={() => handleOpenEdit(addr)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-[#1a365d] hover:bg-[#1a365d]/8 transition-colors"
                  title="Edit address"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(addr?.id)}
                  disabled={deletingId === addr?.id}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Delete address"
                >
                  {deletingId === addr?.id ? (
                    <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AddressManagement;
